import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { get } from '../../helpers/apiHelper';
import publicPageCustomizationService from '../../helpers/publicPageCustomizationHelper';
import ProductosPublicPageContent from '../../components/public/ProductosPublicPageContent';

const ProductosPublicPage = () => {
    const { marcaId } = useParams();
    const [marca, setMarca] = useState(null);
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [categorias, setCategorias] = useState([]);

    // Estados para personalización
    const [texts, setTexts] = useState({
        welcomeModalTitle: '¡Únete al Club de {{brandName}}!',
        welcomeModalDescription: 'Disfruta de beneficios exclusivos y ofertas especiales',
        benefit1Title: 'Descuentos Exclusivos',
        benefit1Description: 'Accede a ofertas especiales solo para miembros',
        benefit2Title: 'Regalo de Cumpleaños',
        benefit2Description: 'Sorpresas especiales en tu día',
        benefit3Title: 'Tarjeta Digital',
        benefit3Description: 'Acumula puntos en cada visita',
        joinButtonText: 'Unirme Ahora',
        maybeLaterButtonText: 'Quizás más tarde',
        floatingButtonText: 'Únete al Club',
        noProductsMessage: 'No se encontraron productos que coincidan con tu búsqueda.',
        searchPlaceholder: 'Buscar...'
    });
    const [styles, setStyles] = useState({});
    const [layout, setLayout] = useState('grid');
    const [layoutOptions, setLayoutOptions] = useState({
        showLogo: true,
        showSlogan: true,
        logoPosition: 'left',
        headerStyle: 'sticky',
        productCardStyle: 'default'
    });
    const [config, setConfig] = useState({
        showWelcomeModal: true,
        welcomeModalDelay: 30,
        showSearchButton: true,
        showFloatingButton: true,
        productsPerCategoryPreview: 3
    });

    // Función para procesar variables en textos
    const processVariables = (text, variables) => {
        if (!text) return '';
        let processed = text;
        Object.keys(variables).forEach(key => {
            const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
            processed = processed.replace(regex, variables[key] || '');
        });
        return processed;
    };

    useEffect(() => {
        let timeoutId;

        async function fetchData() {
            try {
                const [marcaData, productosData, categoriasData, customizationData] = await Promise.all([
                    get(`marcas/${marcaId}`).catch(() => null),
                    get(`productos/${marcaId}/lista?activo=true`).catch(() => []),
                    get(`productos/${marcaId}/categorias?activa=true`).catch(() => []),
                    publicPageCustomizationService.getCustomization(marcaId, 'productos').catch(() => null)
                ]);

                if (!marcaData) {
                    setLoading(false);
                    return;
                }

                setMarca(marcaData);
                setProductos(productosData);
                setCategorias(categoriasData);

                // Procesar personalización
                let processedCustomization = null;
                if (customizationData) {
                    // Manejar objetos Mongoose que pueden tener _doc
                    processedCustomization = customizationData._doc || customizationData;
                }

                // Cargar tipografía desde personalización o marca (solo si es una fuente de Google Fonts)
                const fontFamilyToLoad = processedCustomization?.styles?.fontFamily || marcaData.tipografia;
                if (fontFamilyToLoad) {
                    // Fuentes del sistema que NO deben cargarse desde Google Fonts
                    const systemFonts = ['Arial', 'Helvetica', 'Verdana', 'Tahoma', 'Georgia', 'Times New Roman', 'sans-serif', 'serif', 'monospace'];
                    const fontName = fontFamilyToLoad.split(',')[0].trim().replace(/['"]/g, '');

                    if (!systemFonts.includes(fontName)) {
                    const link = document.createElement('link');
                        link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(' ', '+')}&display=swap`;
                    link.rel = 'stylesheet';
                    document.head.appendChild(link);
                    }
                }

                // Procesar personalización
                if (processedCustomization && processedCustomization.isActive !== false) {

                    // Aplicar textos personalizados (con procesamiento de variables)
                    const variables = {
                        brandName: marcaData.nombre || '',
                        brandSlogan: marcaData.eslogan || ''
                    };

                    const processedTexts = {};
                    const textsToProcess = processedCustomization.texts || {};
                    Object.keys(textsToProcess).forEach(key => {
                        processedTexts[key] = processVariables(textsToProcess[key], variables);
                    });

                    setTexts(prev => ({ ...prev, ...processedTexts }));

                    // Aplicar estilos personalizados
                    if (processedCustomization.styles) {
                        setStyles(processedCustomization.styles);
                    }

                    // Aplicar layout y opciones
                    if (processedCustomization.layout) {
                        setLayout(processedCustomization.layout);
                    } else {
                        setLayout('grid');
                    }
                    if (processedCustomization.layoutOptions) {
                        // Filtrar solo las opciones válidas para productos
                        const validOptions = {
                            showLogo: processedCustomization.layoutOptions.showLogo,
                            showSlogan: processedCustomization.layoutOptions.showSlogan,
                            logoPosition: processedCustomization.layoutOptions.logoPosition,
                            headerStyle: processedCustomization.layoutOptions.headerStyle,
                            productCardStyle: processedCustomization.layoutOptions.productCardStyle
                        };
                        // Eliminar valores undefined
                        Object.keys(validOptions).forEach(key => {
                            if (validOptions[key] === undefined) {
                                delete validOptions[key];
                            }
                        });
                        setLayoutOptions(prev => ({ ...prev, ...validOptions }));
                    }

                    // Aplicar configuración
                    if (processedCustomization.config) {
                        setConfig(prev => ({ ...prev, ...processedCustomization.config }));
                    } else {
                        // Si no hay config en la personalización, usar valores por defecto
                        setConfig({
                            showWelcomeModal: true,
                            welcomeModalDelay: 30,
                            showSearchButton: true,
                            showFloatingButton: true,
                            productsPerCategoryPreview: 3
                        });
                    }
                } else {
                    // Usar valores por defecto, pero procesar variables en textos por defecto
                    const variables = {
                        brandName: marcaData.nombre || '',
                        brandSlogan: marcaData.eslogan || ''
                    };
                    const defaultTexts = { ...texts };
                    Object.keys(defaultTexts).forEach(key => {
                        defaultTexts[key] = processVariables(defaultTexts[key], variables);
                    });
                    setTexts(defaultTexts);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        }

        fetchData();

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [marcaId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-pulse text-gray-600 flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-t-blue-500 border-gray-200 rounded-full animate-spin mb-4"></div>
                    <p className="text-lg">Cargando...</p>
                </div>
            </div>
        );
    }

    if (!marca) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center text-gray-600">
                    <p className="text-xl">Negocio no encontrado</p>
                </div>
            </div>
        );
    }

    return (
        <ProductosPublicPageContent
                marca={marca}
            productos={productos}
            categorias={categorias}
            texts={texts}
            styles={styles}
            layout={layout}
            layoutOptions={layoutOptions}
            config={config}
            isPreview={false}
        />
    );
};

export default ProductosPublicPage; 
